/**
 * Search-term extraction utilities.
 *
 * Recognizes URLs from common search engines (Google, Bing, DuckDuckGo,
 * YouTube, Amazon, eBay, social platforms, etc.) and pulls the analyst-
 * relevant query string out so a calling component can present "what was
 * searched" instead of "what URL was visited".
 *
 * Both the per-domain `EntityDetailsDialog` Searches tab and the graph-wide
 * `NetworkGraph` Search tab share this module so the recognized engines stay
 * in sync as new ones get added.
 */

export interface SearchEngineConfig {
  /** Display label shown on chips / badges. */
  name: string;
  /** Substring match against the URL hostname (lowercased). Any match wins. */
  hostMatches: string[];
  /**
   * Optional path-prefix filter -- only treat the URL as a search when the
   * pathname starts with one of these (e.g. YouTube searches live under
   * `/results`, Google Maps under `/maps`).
   */
  pathPrefixes?: string[];
  /** Query parameter name(s) to try, in priority order. */
  queryParams: string[];
  /** Display color for the engine badge. */
  color: string;
}

export const SEARCH_ENGINES: SearchEngineConfig[] = [
  { name: 'Google',     hostMatches: ['google.'],         queryParams: ['q'],            color: '#4285F4' },
  { name: 'Google Maps', hostMatches: ['google.'], pathPrefixes: ['/maps'], queryParams: ['q'], color: '#34A853' },
  { name: 'Bing',       hostMatches: ['bing.com'],        queryParams: ['q'],            color: '#008373' },
  { name: 'DuckDuckGo', hostMatches: ['duckduckgo.com'],  queryParams: ['q'],            color: '#DE5833' },
  { name: 'Yahoo',      hostMatches: ['search.yahoo.', 'yahoo.com/search'], queryParams: ['p', 'q'], color: '#5F01D1' },
  { name: 'Brave',      hostMatches: ['search.brave.com'], queryParams: ['q'],           color: '#FB542B' },
  { name: 'Ecosia',     hostMatches: ['ecosia.org'],      queryParams: ['q'],            color: '#1B6435' },
  { name: 'Startpage',  hostMatches: ['startpage.com'],   queryParams: ['query', 'q'],   color: '#1E3A5F' },
  { name: 'YouTube',    hostMatches: ['youtube.com'],     pathPrefixes: ['/results'],    queryParams: ['search_query'], color: '#FF0000' },
  { name: 'Amazon',     hostMatches: ['amazon.'],         pathPrefixes: ['/s'],          queryParams: ['k', 'field-keywords'], color: '#FF9900' },
  { name: 'eBay',       hostMatches: ['ebay.'],           pathPrefixes: ['/sch'],        queryParams: ['_nkw'],         color: '#E53238' },
  { name: 'Reddit',     hostMatches: ['reddit.com'],      pathPrefixes: ['/search'],     queryParams: ['q'],            color: '#FF4500' },
  { name: 'X / Twitter', hostMatches: ['twitter.com', 'x.com'], pathPrefixes: ['/search'], queryParams: ['q'],          color: '#000000' },
  { name: 'Facebook',   hostMatches: ['facebook.com'],    pathPrefixes: ['/search'],     queryParams: ['q'],            color: '#1877F2' },
  { name: 'Pinterest',  hostMatches: ['pinterest.'],      pathPrefixes: ['/search'],     queryParams: ['q'],            color: '#E60023' },
  { name: 'TikTok',     hostMatches: ['tiktok.com'],      pathPrefixes: ['/search'],     queryParams: ['q'],            color: '#000000' },
  { name: 'LinkedIn',   hostMatches: ['linkedin.com'],    pathPrefixes: ['/search'],     queryParams: ['keywords', 'q'], color: '#0A66C2' },
];

export interface ExtractedSearch {
  engine: string;
  engineColor: string;
  term: string;
  /** A sample URL (usually the most-recent visit so the link still works). */
  url: string;
  /** Total visit count for this distinct term. */
  count: number;
  /** ms epoch of the most recent visit (0 if unknown). */
  lastVisit: number;
}

/**
 * Try to extract a search term from a URL. Returns null if the URL doesn't
 * look like a search query for any known engine. Search terms are URL-
 * decoded (handles legacy `+` for space) and trimmed; empty results return
 * null.
 */
export function extractSearchTerm(
  rawUrl: string,
): { engine: string; engineColor: string; term: string } | null {
  let parsed: URL;
  try {
    parsed = new URL(rawUrl);
  } catch {
    return null;
  }

  const host = parsed.hostname.toLowerCase();
  const path = parsed.pathname.toLowerCase();

  for (const cfg of SEARCH_ENGINES) {
    if (!cfg.hostMatches.some((h) => host.includes(h))) continue;
    if (cfg.pathPrefixes && !cfg.pathPrefixes.some((p) => path.startsWith(p))) continue;

    for (const param of cfg.queryParams) {
      const raw = parsed.searchParams.get(param);
      if (!raw) continue;
      // URL params often use '+' for spaces (legacy form encoding); normalize.
      const decoded = raw.replace(/\+/g, ' ').trim();
      if (decoded.length === 0) continue;
      return { engine: cfg.name, engineColor: cfg.color, term: decoded };
    }
  }

  return null;
}

/**
 * English stopwords used to clean keyword extraction. Kept short on purpose
 * so we don't strip terms an analyst might actually care about.
 */
export const KEYWORD_STOPWORDS = new Set([
  'the', 'a', 'an', 'and', 'or', 'but', 'if', 'then', 'else', 'as',
  'at', 'by', 'for', 'from', 'in', 'into', 'of', 'on', 'to', 'with', 'without',
  'is', 'are', 'was', 'were', 'be', 'been', 'being', 'am',
  'do', 'does', 'did', 'has', 'have', 'had',
  'i', 'you', 'he', 'she', 'it', 'we', 'they', 'me', 'us', 'them',
  'my', 'your', 'his', 'her', 'its', 'our', 'their',
  'this', 'that', 'these', 'those',
  'how', 'what', 'when', 'where', 'who', 'why', 'which',
  'can', 'will', 'would', 'could', 'should', 'may', 'might', 'must',
  'not', 'no', 'so', 'than', 'too', 'very', 'just',
]);

/**
 * Split a search term into normalized lowercase tokens, filtering stopwords
 * and very short tokens. Apostrophes inside words are preserved (e.g. "don't")
 * but trimmed off the outside.
 */
export function tokenizeKeywords(term: string): string[] {
  return term
    .toLowerCase()
    .split(/[^a-z0-9'’]+/)
    .map((t) => t.replace(/^['’]+|['’]+$/g, ''))
    .filter((t) => t.length >= 3 && !KEYWORD_STOPWORDS.has(t));
}

/**
 * Aggregate a flat list of URLs (each with optional visit count and
 * timestamp) into one row per `engine|term`. Repeat searches for the same
 * term collapse to a single row with a summed count.
 */
export function aggregateSearches(
  urls: Array<{ url: string; count?: number; lastVisit?: number | string | null }>,
): ExtractedSearch[] {
  const grouped = new Map<string, ExtractedSearch>();
  for (const u of urls) {
    const info = extractSearchTerm(u.url);
    if (!info) continue;
    const key = `${info.engine}|${info.term.toLowerCase()}`;
    const lastVisitMs =
      typeof u.lastVisit === 'number'
        ? u.lastVisit
        : u.lastVisit
        ? new Date(u.lastVisit).getTime() || 0
        : 0;
    const count = u.count ?? 1;
    const existing = grouped.get(key);
    if (existing) {
      existing.count += count;
      if (lastVisitMs > existing.lastVisit) {
        existing.lastVisit = lastVisitMs;
        // Sample URL = most-recent visit so the click-through link still works.
        existing.url = u.url;
      }
    } else {
      grouped.set(key, {
        engine: info.engine,
        engineColor: info.engineColor,
        term: info.term,
        url: u.url,
        count,
        lastVisit: lastVisitMs,
      });
    }
  }
  return Array.from(grouped.values());
}
